import { createPortal } from "react-dom";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { completeSessionExpiredLogin } from "@/lib/authSession";
import { useUiStore, type ActionFeedbackTone } from "@/store/uiStore";

const BRAND_CHROME = {
  header: "bg-[#009BF5]/5 border-[#009BF5]/25",
  iconWrap: "bg-[#009BF5]/10",
  title: "text-slate-900",
  subtitle: "text-[#009BF5]",
  button: "bg-[#009BF5] hover:bg-[#0085d4] shadow-lg shadow-[#009BF5]/20",
  close: "text-slate-500 hover:bg-[#009BF5]/10 hover:text-[#009BF5]",
};

const TONE_ICON_COLOR: Record<ActionFeedbackTone, string> = {
  success: "text-[#009BF5]",
  error: "text-red-600",
  warning: "text-amber-600",
  info: "text-[#009BF5]",
};

function ToneIcon({ tone }: { tone: ActionFeedbackTone }) {
  const className = "w-5 h-5";
  if (tone === "success") return <CheckCircle2 className={className} />;
  if (tone === "warning") return <AlertTriangle className={className} />;
  if (tone === "info") return <Info className={className} />;
  return <AlertCircle className={className} />;
}

function subtitleForTone(tone: ActionFeedbackTone): string {
  if (tone === "success") return "Your changes were applied successfully";
  if (tone === "warning") return "Please review the message below";
  if (tone === "info") return "Here is some information for you";
  return "Please review the message below";
}

export function ActionFeedbackModal() {
  const actionFeedback = useUiStore((s) => s.actionFeedback);
  const dismissActionFeedback = useUiStore((s) => s.dismissActionFeedback);

  if (!actionFeedback?.open) return null;

  const iconColor = TONE_ICON_COLOR[actionFeedback.tone];
  const sessionExpired = actionFeedback.sessionExpired === true;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/45"
      role="presentation"
      onClick={(e) => {
        if (sessionExpired) return;
        if (e.target === e.currentTarget) dismissActionFeedback();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden ring-1 ring-[#009BF5]/10"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="action-feedback-title"
        aria-describedby="action-feedback-message"
      >
        <div
          className={cn(
            "flex items-center justify-between px-5 py-4 border-b",
            BRAND_CHROME.header,
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
                BRAND_CHROME.iconWrap,
                iconColor,
              )}
            >
              <ToneIcon tone={actionFeedback.tone} />
            </div>
            <div className="min-w-0">
              <div
                id="action-feedback-title"
                className={cn("font-bold text-sm", BRAND_CHROME.title)}
              >
                {actionFeedback.title}
              </div>
              <div className={cn("text-xs", BRAND_CHROME.subtitle)}>
                {subtitleForTone(actionFeedback.tone)}
              </div>
            </div>
          </div>
          {sessionExpired ? null : (
            <button
              type="button"
              onClick={dismissActionFeedback}
              className={cn("p-1.5 rounded-lg transition-colors shrink-0", BRAND_CHROME.close)}
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div
          id="action-feedback-message"
          className="px-5 py-4 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap"
        >
          {actionFeedback.message}
        </div>

        {actionFeedback.code ? (
          <div className="px-5 pb-2 text-[11px] font-mono text-[#009BF5]/60">
            {actionFeedback.code}
          </div>
        ) : null}

        <div className="px-5 py-4 border-t border-slate-100 flex justify-end bg-slate-50/50">
          <button
            type="button"
            onClick={sessionExpired ? completeSessionExpiredLogin : dismissActionFeedback}
            className={cn(
              "px-4 py-2 text-white text-sm font-bold rounded-xl transition-colors",
              BRAND_CHROME.button,
            )}
          >
            {sessionExpired ? "Log in" : "OK"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
